import { IButtonOptions } from "devextreme-react/button";
import {
  ButtonItem,
  Form,
  FormRef,
  GroupItem,
  SimpleItem,
} from "devextreme-react/form";
import { Popup } from "devextreme-react/popup";
import { ISelectBoxOptions } from "devextreme-react/select-box";
import { ITextBoxOptions } from "devextreme-react/text-box";
import notify from "../../lib/notify";
import { FormEvent, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createDocumentSendRequest } from "../../api/document-send-requests";

type DocumentSendRequestCreatePopupProps = {
  handleCreated: () => void;
  handleClose: () => void;
};

export default function DocumentSendRequestCreatePopup({
  handleCreated,
  handleClose,
}: DocumentSendRequestCreatePopupProps) {
  const { t } = useTranslation();

  const formData = useRef({
    documentType: 0, // 0 - kwitek wypłaty, 1 - PIT
    year: String(new Date().getFullYear()),
    month: String(new Date().getMonth() + 1).padStart(2, "0"),
    emailAddress: "",
  });
  const formRef = useRef<FormRef>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isMonthSelectBoxVisible, setIsMonthSelectBoxVisible] = useState(true);

  const yearSelectBoxDataSource = useMemo(() => {
    const dataSource: { value: string; label: string }[] = [];
    for (
      let year = new Date().getFullYear() - 5;
      year <= new Date().getFullYear();
      year++
    ) {
      dataSource.push({
        value: String(year),
        label: String(year),
      });
    }

    return dataSource;
  }, []);

  const monthSelectBoxDataSource = useMemo(() => {
    const dataSource: { value: string; label: string }[] = [];
    for (let month = 0; month < 12; month++) {
      dataSource.push({
        value: String(month + 1).padStart(2, "0"),
        label: t("monthsList", { returnObjects: true })[month],
      });
    }

    return dataSource;
  }, [t]);

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setIsLoading(true);
      createDocumentSendRequest(
        formData.current.documentType,
        formData.current.year,
        formData.current.month,
        formData.current.emailAddress
      )
        .then(handleCreated)
        .catch((error: Error) => {
          notify(error.message, "error", 5000);
        })
        .finally(() => setIsLoading(false));
    },
    [handleCreated]
  );

  return (
    <Popup
      title={t("documentSendRequests.createPopup.title")}
      maxWidth={500}
      height="auto"
      maxHeight="80vh"
      visible
      showCloseButton
      dragEnabled
      onHiding={handleClose}
      disabled={isLoading}
    >
      <form onSubmit={handleFormSubmit}>
        <Form
          ref={formRef}
          formData={formData.current}
          onFieldDataChanged={({ dataField, value }) => {
            if (dataField === "documentType") {
              if (value === 1) {
                setIsMonthSelectBoxVisible(false);
              } else {
                setIsMonthSelectBoxVisible(true);
              }
            }
          }}
        >
          <SimpleItem
            dataField="documentType"
            label={{
              text: t("documentSendRequests.createPopup.documentType"),
            }}
            editorType="dxSelectBox"
            editorOptions={
              {
                dataSource: [
                  {
                    value: 0,
                    label: t("documentSendRequests.documentTypes.payslip"),
                  },
                  {
                    value: 1,
                    label: t("documentSendRequests.documentTypes.pit"),
                  },
                ],
                valueExpr: "value",
                displayExpr: "label",
              } as ISelectBoxOptions
            }
            isRequired
          />
          <GroupItem colCount={2}>
            <SimpleItem
              dataField="year"
              label={{
                text: t("documentSendRequests.createPopup.year"),
              }}
              editorType="dxSelectBox"
              editorOptions={
                {
                  dataSource: yearSelectBoxDataSource,
                  valueExpr: "value",
                  displayExpr: "label",
                } as ISelectBoxOptions
              }
              isRequired
            />
            <SimpleItem
              dataField="month"
              label={{
                text: t("documentSendRequests.createPopup.month"),
              }}
              editorType="dxSelectBox"
              editorOptions={
                {
                  dataSource: monthSelectBoxDataSource,
                  valueExpr: "value",
                  displayExpr: "label",
                } as ISelectBoxOptions
              }
              isRequired
              visible={isMonthSelectBoxVisible}
            />
          </GroupItem>
          <SimpleItem
            dataField="emailAddress"
            label={{
              text: t("documentSendRequests.createPopup.emailAddress"),
            }}
            editorType="dxTextBox"
            editorOptions={
              {
                mode: "email",
              } as ITextBoxOptions
            }
            isRequired
          />
          <ButtonItem
            buttonOptions={
              {
                useSubmitBehavior: true,
                text: t("documentSendRequests.createPopup.submitButton"),
                type: "default",
              } as IButtonOptions
            }
          />
        </Form>
      </form>
    </Popup>
  );
}
